import {useEffect, useState} from "react";
import {Box, Stack, Typography, Button, Select, MenuItem, Slider, Switch, CircularProgress, Skeleton} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CpuIcon, FloppyDiskIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {KnowledgeManagerRepository} from "../../../../repositories/KnowledgeManagerRepository";
import {Panel} from "../../dashboard/components/Panel";

const selectSx = {borderRadius: "10px", bgcolor: "#FAFAFD", fontSize: 13, "& .MuiOutlinedInput-notchedOutline": {border: "none"}};
const sliderSx = {color: "#8491F7", "& .MuiSlider-thumb": {width: 14, height: 14}};
const switchSx = {"& .MuiSwitch-switchBase.Mui-checked": {color: "#8491F7"}, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {bgcolor: "#8491F7"}};

const LLM_MODELS = [
  {value: "gpt-4o-mini", label: "GPT-4o Mini"},
  {value: "gpt-4o", label: "GPT-4o"},
  {value: "gpt-4-turbo", label: "GPT-4 Turbo"},
  {value: "claude-3-sonnet", label: "Claude 3 Sonnet"},
  {value: "claude-3-haiku", label: "Claude 3 Haiku"},
  {value: "llama-3.1-70b", label: "Llama 3.1 70B"},
  {value: "ollama/custom", label: "Ollama (Custom)"},
];

const EMBEDDING_MODELS = [
  {value: "text-embedding-3-small", label: "text-embedding-3-small"},
  {value: "text-embedding-3-large", label: "text-embedding-3-large"},
  {value: "text-embedding-ada-002", label: "text-embedding-ada-002"},
  {value: "nomic-embed-text", label: "nomic-embed-text"},
];

// The backend's configured default can be any string (e.g. a newer model not in our hardcoded
// list yet) - if the current value isn't one of the known options, add it so the Select has
// something to render instead of going blank.
const withCurrentValue = (options, current) =>
  current && !options.some(o => o.value === current) ? [...options, {value: current, label: current}] : options;

export const SettingsTab = ({topic}) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    KnowledgeManagerRepository.getConfig(topic.id)
      .then(c => {if (!cancelled) setConfig(c);})
      .catch(() => {if (!cancelled) setConfig({...topic.config});})
      .finally(() => {if (!cancelled) setLoading(false);});
    return () => {cancelled = true;};
  }, [topic.id, topic.config]);

  const update = (key, value) => setConfig(prev => ({...prev, [key]: value}));

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await KnowledgeManagerRepository.upsertConfig(topic.id, config);
      setConfig(saved);
      toast.success("LightRAG configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <Panel sx={{p: 3}}>
        <Stack gap={2}>
          {Array.from({length: 5}).map((_, i) => <Skeleton key={i} height={32}/>)}
        </Stack>
      </Panel>
    );
  }

  return (
    <Panel sx={{p: 0}}>
      <Box sx={{px: 3, pt: 2.5, pb: 1}}>
        <Stack direction="row" alignItems="center" gap={1}>
          <HugeiconsIcon icon={CpuIcon} size={16} color={"#8491F7"}/>
          <Typography sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>LightRAG Configuration</Typography>
        </Stack>
      </Box>
      <Box sx={{p: 3, pt: 1}}>
        <Stack gap={3}>

          <Stack direction={{xs: "column", sm: "row"}} gap={2}>
            <Box sx={{flex: 1}}>
              <Typography sx={{fontSize: 12, color: "#6B6B80", mb: 0.75}}>LLM Model</Typography>
              <Select fullWidth size="small" value={config.llmModel} onChange={(e) => update("llmModel", e.target.value)} sx={selectSx}>
                {withCurrentValue(LLM_MODELS, config.llmModel).map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </Box>
            <Box sx={{flex: 1}}>
              <Typography sx={{fontSize: 12, color: "#6B6B80", mb: 0.75}}>Embedding Model</Typography>
              <Select fullWidth size="small" value={config.embeddingModel} onChange={(e) => update("embeddingModel", e.target.value)} sx={selectSx}>
                {withCurrentValue(EMBEDDING_MODELS, config.embeddingModel).map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </Box>
          </Stack>

          <Box>
            <Typography sx={{fontSize: 12, color: "#8A8AA0", mb: 1.5}}>Chunking Settings</Typography>
            <Stack direction={{xs: "column", sm: "row"}} gap={{xs: 2.5, sm: 4}}>
              <Box sx={{flex: 1}}>
                <Typography sx={{fontSize: 12, color: "#6B6B80", mb: 1}}>Chunk Size: {config.chunkSize}</Typography>
                <Slider value={config.chunkSize} onChange={(e, v) => update("chunkSize", v)} min={200} max={4000} step={100} size="small" sx={sliderSx}/>
              </Box>
              <Box sx={{flex: 1}}>
                <Typography sx={{fontSize: 12, color: "#6B6B80", mb: 1}}>Chunk Overlap: {config.chunkOverlap}</Typography>
                <Slider value={config.chunkOverlap} onChange={(e, v) => update("chunkOverlap", v)} min={0} max={500} step={10} size="small" sx={sliderSx}/>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography sx={{fontSize: 12, color: "#8A8AA0", mb: 1.5}}>Generation Settings</Typography>
            <Stack direction={{xs: "column", sm: "row"}} gap={{xs: 2.5, sm: 4}}>
              <Box sx={{flex: 1}}>
                <Typography sx={{fontSize: 12, color: "#6B6B80", mb: 1}}>Max Tokens: {config.maxTokens}</Typography>
                <Slider value={config.maxTokens} onChange={(e, v) => update("maxTokens", v)} min={256} max={16384} step={256} size="small" sx={sliderSx}/>
              </Box>
              <Box sx={{flex: 1}}>
                <Typography sx={{fontSize: 12, color: "#6B6B80", mb: 1}}>Temperature: {config.temperature.toFixed(2)}</Typography>
                <Slider value={config.temperature * 100} onChange={(e, v) => update("temperature", v / 100)} min={0} max={200} step={5} size="small" sx={sliderSx}/>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography sx={{fontSize: 12, color: "#8A8AA0", mb: 1.5}}>Graph Settings</Typography>
            <Stack gap={2.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{fontSize: 12, color: "#23264D"}}>Enable Graph Search</Typography>
                  <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>Use knowledge graph for enhanced retrieval</Typography>
                </Box>
                <Switch checked={config.enableGraphSearch} onChange={(e) => update("enableGraphSearch", e.target.checked)} sx={switchSx}/>
              </Stack>
              <Box>
                <Typography sx={{fontSize: 12, color: "#6B6B80", mb: 1}}>Community Level: {config.graphCommunityLevel}</Typography>
                <Slider value={config.graphCommunityLevel} onChange={(e, v) => update("graphCommunityLevel", v)} min={1} max={5} step={1} size="small" sx={sliderSx}/>
              </Box>
              <Box>
                <Typography sx={{fontSize: 12, color: "#6B6B80", mb: 1}}>Top K: {config.topK}</Typography>
                <Slider value={config.topK} onChange={(e, v) => update("topK", v)} min={1} max={200} step={1} size="small" sx={sliderSx}/>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Button
              startIcon={saving ? <CircularProgress size={14} sx={{color: "#fff"}}/> : <HugeiconsIcon icon={FloppyDiskIcon} size={14}/>}
              onClick={handleSave}
              disabled={saving}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'},
                '&.Mui-disabled': {bgcolor: '#D1D5DB', color: '#9CA3AF'},
              }}
            >
              {saving ? "Saving..." : "Save Configuration"}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Panel>
  );
};
